import PropTypes from 'prop-types';
import classNames from 'classnames';

import { legacyTheme, styled } from '@prose-ui/legacy';
import { makeStyles } from 'legacyStyles';

import BlockLayout from 'Blocks/BlockLayout';

import { srcOrNextImageProp } from 'PropTypes/imageProps';

import ProductsItem from './ProductsItem';

const useStyles = makeStyles(() => ({
  content: {
    marginTop: 0,
    marginBottom: 0,
  },
}));

const Products = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: ${({ justifyContent }) => justifyContent};
  align-items: flex-start;
  width: 100%;
  gap: 24px;

  ${legacyTheme.breakpoints.up('md')} {
    gap: ${({ listLength }) => (listLength <= 3 ? 24 : 0)}px;
  }
`;

const ProductContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  width: ${({ productsToDisplayOnARow }) => `calc(${100 / productsToDisplayOnARow[0]}% - 12px)`};

  ${legacyTheme.breakpoints.up('md')} {
    width: ${({ productsToDisplayOnARow }) => `calc(${100 / productsToDisplayOnARow[1]}% - 16px)`};
  }
  ${legacyTheme.breakpoints.up('lg')} {
    width: ${({ productsToDisplayOnARow }) => `calc(${100 / productsToDisplayOnARow[2]}% - 18px)`};
  }
`;

const ProductsGrid = ({
  background,
  content,
  dataFrom,
  productsToDisplayOnARow,
  justifyContent,
}) => {
  const { classes } = useStyles();
  return (
    <BlockLayout
      backgroundColor={background}
      classes={{ content: classNames(classes.content) }}
      contentAlign="center"
      contentWidth="veryWide"
    >
      <Products justifyContent={justifyContent} listLength={content?.cards?.length}>
        {content.cards.map(
          ({
            currency,
            slug,
            productPageUrl,
            bgLabelledPicture,
            bgPicture,
            bgPictureAlt,
            label,
            capacity,
            priceWithSubscription,
            regularPrice,
            shortLabel = '',
            useShortLabel = false,
            isCustom = true,
            isSubscribable,
            hasMultiplePrices = false,
            dataClickOnCollection,
            showCapacity,
            tag,
            showPrice,
            subLabel,
          }) => (
            <ProductContainer key={slug} productsToDisplayOnARow={productsToDisplayOnARow}>
              <ProductsItem
                bgLabelledPicture={bgLabelledPicture}
                bgPicture={bgPicture}
                bgPictureAlt={bgPictureAlt}
                capacity={capacity}
                currency={currency}
                dataClick={dataClickOnCollection}
                dataFrom={dataFrom}
                hasMultiplePrices={Boolean(hasMultiplePrices || isSubscribable)}
                label={
                  isCustom
                    ? `Custom ${useShortLabel ? shortLabel : label}`
                    : useShortLabel
                    ? shortLabel
                    : label
                }
                price={isSubscribable ? priceWithSubscription : regularPrice}
                productPageUrl={productPageUrl}
                showCapacity={showCapacity}
                showPrice={showPrice}
                slug={slug}
                subLabel={subLabel}
                tag={tag}
              />
            </ProductContainer>
          )
        )}
      </Products>
    </BlockLayout>
  );
};

ProductsGrid.defaultProps = {
  productsToDisplayOnARow: [2, 3, 4],
  background: 'white',
  dataFrom: 'products-block',
  justifyContent: 'flex-start',
};

ProductsGrid.propTypes = {
  background: PropTypes.string,
  content: PropTypes.shape({
    cards: PropTypes.arrayOf(
      PropTypes.shape({
        bgLabelledPicture: srcOrNextImageProp.isRequired,
        bgPicture: srcOrNextImageProp.isRequired,
        bgPictureAlt: PropTypes.string,
        capacity: PropTypes.string,
        label: PropTypes.string.isRequired,
        price: PropTypes.number.isRequired,
        productPageUrl: PropTypes.string.isRequired,
        slug: PropTypes.string.isRequired,
        hasMultiplePrice: PropTypes.number,
        dataClickOnCollection: PropTypes.string,
      })
    ).isRequired,
  }).isRequired,
  dataFrom: PropTypes.string,
  justifyContent: PropTypes.string,
  productsToDisplayOnARow: PropTypes.arrayOf(PropTypes.number),
};

export default ProductsGrid;
